import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Crown, Check, Lock, ArrowClockwise, SoccerBall, ShieldCheck, CreditCard, EnvelopeSimple } from "@phosphor-icons/react";
import KickoffCountdown from "../components/KickoffCountdown";

const PREMIER_BG = "https://images.pexels.com/photos/11953753/pexels-photo-11953753.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FREE_FEATURES = [
  "Predict every Premier League fixture",
  "10 + total goals for an exact score; 5 for correct result",
  "Compete on the global leaderboard",
  "Favourite-team badge on your row",
  "Club Tribes leaderboard",
];
const FREE_LIMITS = [
  "Fixtures visible only 7 days before kickoff",
  "Cannot create private leagues",
];

const PREMIER_FEATURES = [
  "Everything in Free Basic",
  "See the FULL season's fixtures upfront",
  "Create unlimited private leagues",
  "Detailed stats & form charts on your profile",
  "Premier badge next to your nickname",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!user || user.is_premier) return;
    (async () => {
      try {
        const { data } = await apiClient.get("/payments/me/pending");
        setPending(data.pending);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn(err);
      }
    })();
  }, [user]);

  const startCheckout = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/payments/checkout", {
        package_id: "premier_season",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const isPremier = user?.is_premier;

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${PREMIER_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C]/85 to-[#090A0C]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Membership</span>
          <h1 className="font-heading text-5xl sm:text-6xl uppercase font-black mt-3">Pricing</h1>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">
            One season pass. £9.99. No hidden tiers. Cancel any time — it&apos;s a one-off.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {!isPremier && <KickoffCountdown variant="card" />}
        {pending && (
          <div data-testid="resume-checkout-banner" className="mb-10 border border-primary-yellow/40 bg-primary-yellow/5 p-6 flex items-start gap-4">
            <ArrowClockwise size={28} weight="bold" className="text-primary-yellow shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-heading text-xl uppercase font-black">Resume your checkout</h3>
              <p className="text-white/60 text-sm mt-1">
                Your <span className="text-primary-yellow font-bold">{pending.package_label}</span> purchase
                ({pending.currency?.toUpperCase()} {pending.amount?.toFixed?.(2)}) wasn&apos;t completed.
              </p>
            </div>
            <button
              data-testid="resume-checkout-cta"
              onClick={startCheckout}
              disabled={loading}
              className="bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold px-5 py-3 text-xs hover:-translate-y-0.5 transition-transform disabled:opacity-50"
            >
              {loading ? "Loading…" : "Resume"}
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {/* Free Basic */}
          <div data-testid="tier-free" className="surface p-8 lg:p-10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-2 text-white/50">
              <SoccerBall size={12} weight="fill" /> Free Basic
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-heading text-5xl lg:text-6xl font-black font-mono-num">£0</div>
              <div className="text-white/40 text-sm">/ forever</div>
            </div>
            <p className="text-white/60 mt-3 text-sm">For the casual forecaster.</p>
            <ul className="mt-8 space-y-3 text-sm">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check size={16} weight="bold" className="text-primary-yellow mt-0.5 shrink-0" /> {f}
                </li>
              ))}
              {FREE_LIMITS.map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/40">
                  <Lock size={14} weight="bold" className="text-white/30 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {!isPremier && (
              <div data-testid="tier-free-active" className="mt-10 w-full text-center py-4 text-sm uppercase tracking-[0.2em] font-bold bg-white/5 border border-white/10 text-white/60">
                Your tier
              </div>
            )}
            {isPremier && (
              <button disabled className="mt-10 w-full border border-white/10 text-white/40 uppercase tracking-[0.2em] font-semibold py-4 text-sm">
                Downgrade unavailable
              </button>
            )}
          </div>

          {/* Premier */}
          <div data-testid="tier-premier" className="surface-elevated border border-primary-yellow/30 glow-yellow relative p-8 lg:p-10">
            <span className="absolute -top-3 left-8 px-3 py-1 bg-primary-yellow text-black text-[10px] uppercase tracking-[0.25em] font-bold">
              Recommended
            </span>
            <span className="absolute top-6 right-6 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-primary-yellow text-primary-yellow text-[10px] uppercase tracking-[0.25em] font-black bg-primary-yellow/5">
              <span className="font-mono-num">1</span> Season · <span className="font-mono-num">365</span> Days
            </span>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-2 text-primary-yellow">
              <Crown size={12} weight="fill" /> Premier
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-heading text-5xl lg:text-6xl font-black font-mono-num text-primary-yellow">£9.99</div>
              <div className="text-white/40 text-sm">/ season</div>
            </div>
            <p className="text-white/60 mt-3 text-sm">For the title chasers · pay once, 365 days of full access.</p>
            <ul className="mt-8 space-y-3 text-sm">
              {PREMIER_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check size={16} weight="bold" className="text-primary-yellow mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>

            {/* Trust bar — visible right above the £9.99 button where the buying decision happens */}
            {!isPremier && (
              <div data-testid="trust-bar" className="mt-8 grid grid-cols-2 gap-2 p-4 bg-[#0B0D11]/60 border border-white/5">
                <TrustChip Icon={ShieldCheck} text="Stripe-secured checkout" />
                <TrustChip Icon={CreditCard} text="No card details stored" />
                <TrustChip Icon={Lock} text="Bcrypt-hashed passwords" />
                <TrustChip Icon={EnvelopeSimple} text="Verified email accounts" />
              </div>
            )}

            {isPremier ? (
              <div data-testid="tier-premier-active" className="mt-10 w-full text-center py-4 text-sm uppercase tracking-[0.2em] font-bold bg-primary-yellow/10 border border-primary-yellow/40 text-primary-yellow">
                You&apos;re Premier
                {user?.premier_expires_at && (
                  <div className="text-[10px] text-white/50 normal-case tracking-normal mt-1">
                    Active until {new Date(user.premier_expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="tier-premier-cta"
                onClick={startCheckout}
                disabled={loading}
                className="mt-10 w-full bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] py-4 text-sm hover:-translate-y-0.5 transition-transform flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Lock size={14} weight="bold" />
                {loading ? "Redirecting…" : "Go Premier · £9.99"}
              </button>
            )}
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-white/40 uppercase tracking-widest">
          Secure checkout via Stripe · GBP · One-time payment, 365 days access
        </p>
        <p className="mt-3 text-center text-[10px] text-white/30 tracking-wider px-4 max-w-xl mx-auto">
          By purchasing, you agree to our{" "}
          <a href="/terms" className="text-white/60 hover:text-primary-yellow underline">Terms</a>,{" "}
          <a href="/privacy" className="text-white/60 hover:text-primary-yellow underline">Privacy Policy</a>, and{" "}
          <a href="/refund" className="text-white/60 hover:text-primary-yellow underline">Refund Policy</a>.
          14-day cooling-off period applies.
        </p>
      </div>
    </div>
  );
}

const TrustChip = ({ Icon, text }) => (
  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">
    <Icon size={14} weight="fill" className="text-primary-yellow shrink-0" />
    <span className="truncate">{text}</span>
  </div>
);
